import { Download, Columns3, Loader2, SlidersHorizontal, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { cn } from '@/lib/utils';
import { SearchInput } from '@/components/molecules';

interface DataTableToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  showClearAll: boolean;
  onClearAll: () => void;
  activeFilterCount: number;
  onOpenColumns: () => void;
  onOpenFilters: () => void;
  /** Content that occupies the search area when search is hidden. */
  leadingContent?: ReactNode;
  extraActions?: ReactNode;
  /** Controls that belong beside the search, after it (e.g. an owner picker). */
  afterSearch?: ReactNode;
  className?: string;
  searchClassName?: string;
  /** Hide the search control for grids that provide their own query controls. */
  showSearch?: boolean;
  /** Hide the filter drawer trigger when filtering is owned outside the grid. */
  showFilters?: boolean;
  showExport?: boolean;
  exporting: boolean;
  onExportXLSX: () => void;
  onExportCSV: () => void;
}

export function DataTableToolbar({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  showClearAll,
  onClearAll,
  activeFilterCount,
  onOpenColumns,
  onOpenFilters,
  leadingContent,
  extraActions,
  afterSearch,
  className,
  searchClassName,
  showSearch = true,
  showFilters = true,
  showExport = true,
  exporting,
  onExportXLSX,
  onExportCSV,
}: DataTableToolbarProps) {
  return (
    <div className={cn('flex shrink-0 items-center gap-2 px-4 py-2.5', className)}>
      {showSearch ? (
        <>
          {/* Beside the search, not instead of it: a view switch like Call logs'
              Calls | Conferences was dropped here whenever the search showed. */}
          {leadingContent ? <div className="shrink-0">{leadingContent}</div> : null}
          {/* With a switch beside it the search stays compact on the left, so the
              two read as one group and the actions keep to the right. */}
          <div
            className={cn(
              leadingContent || afterSearch ? 'w-full min-w-0 max-w-xs' : 'flex-1',
              searchClassName,
            )}
          >
            <SearchInput
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 text-sm"
            />
          </div>
          {afterSearch ? <div className="shrink-0">{afterSearch}</div> : null}
        </>
      ) : (
        <div className="min-w-0 flex-1">{leadingContent}</div>
      )}
      {showClearAll ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={onClearAll}
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
          title="Clear all filters"
          aria-label="Clear all filters"
        >
          <X size={13} />
        </Button>
      ) : null}
      {showSearch && (leadingContent || afterSearch) ? <div className="flex-1" /> : null}

      <div className="flex shrink-0 items-center gap-1.5">
        {extraActions}
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={onOpenColumns}
          title="Show/hide columns"
          aria-label="Show/hide columns"
        >
          <Columns3 size={13} />
        </Button>
        {showFilters ? (
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'relative h-8 gap-1.5 px-2.5',
              activeFilterCount > 0 && 'border-primary text-primary',
            )}
            onClick={onOpenFilters}
            title="Filter records"
            aria-label="Filter records"
          >
            <SlidersHorizontal size={13} />
            <span className="hidden sm:inline">Filters</span>
            {activeFilterCount > 0 ? (
              <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-[9px] font-semibold text-primary-foreground">
                {activeFilterCount}
              </span>
            ) : null}
          </Button>
        ) : null}
        {showExport ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={exporting}
                title="Export"
              >
                {exporting ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Download size={13} />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onExportXLSX}>
                <Download className="mr-2 size-3.5" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onExportCSV}>
                <Download className="mr-2 size-3.5" />
                CSV (.csv)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}
